import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store/store';
import { actions as _act } from '@/models/store.actions';
import { isMobile } from 'mobile-device-detect';
import { isNativeApp } from '@/helpers/platform-helper';
import { AppModules, AppModuleUsageType, eModuleTypes, Permissions, RouterView } from '@/models/enums';
import { globalSettingsHelper } from '@/helpers/settings-helper';
import { documentService } from '@/services';
import { showLoading } from '@/mixins/ui-mixin';
import { rolesHelper } from '@/helpers/roles.helper';
import { permissionsHelper } from '@/helpers/permissions.helper';
import { isDevelopment } from '@/helpers';
import { userModulesHelper } from '@/helpers/modules-helper';

Vue.use(Router);

const routerMode = isNativeApp() ? 'hash' : 'history';

const router = new Router({
  mode: routerMode,
  routes: [
    { path: '/', name: RouterView.Root, redirect: '/home' },
    {
      path: '/home',
      name: RouterView.Home,
      meta: { requiresAuth: true, requireActions: [10100] },
      component: () => import(/* webpackChunkName: "home" */ '../views/Home.vue')
    },
    {
      path: '/contacts',
      name: 'contacts',
      components: {
        cached: () => import(/* webpackChunkName: "contacts" */ '../views/Contacts.vue')
      }
    },
    {
      path: '/login/:email?',
      name: RouterView.Login,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */ '../views/Auth/Login.vue')
    },
    {
      path: '/roumingreg',
      name: RouterView.RoumingReg,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "rouming" */ '../views/Auth/RoumingRegistration.vue')
    },
    {
      path: '/rouminglogin',
      name: RouterView.RoumingLogin,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "rouming" */ '../views/Auth/RoumingLogin.vue')
    },
    {
      path: '/registration',
      name: RouterView.Registration,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */ '../views/Auth/Registration.vue')
    },
    {
      path: '/registration-completion',
      name: RouterView.RegistrationCompletion,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "auth.registration-completion" */ '../views/Auth/RegistrationCompletion.vue')
    },
    {
      path: '/restore-pass',
      name: RouterView.RestorePass,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */ '../views/Auth/RestorePass.vue')
    },
    {
      path: '/confirm-email',
      name: RouterView.ConfirmEmail,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */ '../views/Auth/ConfirmEmail.vue')
    },
    {
      path: '/account-restore',
      name: RouterView.AccountRestore,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */'../views/Auth/RestoreAccount.vue')
    },
    {
      path: '/confirm-restore-account',
      name: RouterView.ConfirmRestoreAccount,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */'../views/Auth/ConfirmRestoreAccount.vue')
    },
    {
      path: '/remove-account',
      name: RouterView.ConfirmRemoveAccount,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */'../views/Auth/ConfirmRemoveAccount.vue')
    },
    {
      path: '/confirm-email-org-user',
      name: RouterView.ConfirmEmailUserOrg,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */'../views/Auth/ConfirmEmailOrganisationUser.vue')
    },
    {
      path: '/add-new-sign',
      name: 'addNewSign',
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */ '../views/Auth/AddNewSign.vue')
    },
    {
      path: '/add-new-sign-confirm',
      name: 'addNewSignConfirm',
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */ '../views/Auth/AddNewSignConfirm.vue')
    },
    {
      path: '/cabinet',
      redirect: '/cabinet/personal',
      name: 'cabinet',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "cabinet" */ '../views/Cabinet/Cabinet.vue'),
      children: [
        {
          path: 'personal',
          name: 'personal',
          meta: { requiresAuth: true },
          component: () => import(/* webpackChunkName: "cabinet" */ '../views/Cabinet/PersonalData.vue')
        },
        {
          path: 'tariffs',
          name: 'tariffs',
          meta: { requiresAuth: true, requireActions: [10900] },
          component: () => import(/* webpackChunkName: "cabinet.tariffs" */ '../views/Cabinet/Tariffs.vue'),
          children: [
            {
              path: 'order-personal-plan',
              name: 'orderPersonalPlan',
              meta: { requiresAuth: true, requireActions: [10900] },
              component: () => import(/* webpackChunkName: "cabinet.tariffs.order" */ '../views/Cabinet/OrderPersonalTariffPlan.vue')
            }
          ]
        },
        {
          path: 'transactions',
          name: 'transactions',
          meta: { requiresAuth: true, requireActions: [10500] },
          component: () => import(/* webpackChunkName: "cabinet.transactions" */ '../views/Cabinet/Transactions.vue')
        },
        {
          path: 'service-orders',
          name: 'service-orders',
          meta: { requiresAuth: true, requireActions: [10560] },
          component: () => import(/* webpackChunkName: "cabinet.service-orders" */ '../views/Cabinet/ServiceOrders.vue')
        },
        {
          path: 'invitations',
          name: RouterView.Invitations,
          meta: { requiresAuth: true },
          component: () => import(/* webpackChunkName: "cabinet.invitations" */ '../views/Cabinet/Invitations.vue')
        },
        {
          path: 'shablons',
          name: 'shablons',
          meta: { requiresAuth: true, requireActions: [12000] },
          component: () => import(/* webpackChunkName: "cabinet.shablons" */ '../views/Cabinet/Shablons.vue')
        },
        {
          path: 'settings',
          name: 'settings',
          meta: { requiresAuth: true },
          component: () => import(/* webpackChunkName: "cabinet.settings" */ '../views/Cabinet/Settings.vue')
        },
        {
          path: 'stamps',
          name: RouterView.Stamps,
          meta: { requiresAuth: true, requireActions: [14500] },
          component: () => import(/* webpackChunkName: "cabinet.stamps" */ '../views/Cabinet/Stamps.vue')
        },
        {
          path: 'download-tasks',
          name: 'downloadTasks',
          meta: { requiresAuth: true, requireActions: [40000] },
          component: () => import(/* webpackChunkName: "cabinet.downloadTasks" */ '../views/Cabinet/DownloadTasks.vue')
        },
        {
          path: 'modules',
          name: 'userModules',
          redirect: { name: 'userModulesList' },
          component: () => import(/* webpackChunkName: "modules" */ '../views/Cabinet/UserModules.vue'),
          children: [
            {
              path: '',
              name: 'userModulesList',
              meta: { requiresAuth: true, requireActions: [Permissions.MODULESUSE] },
              props: { moduleUsageType: AppModuleUsageType.Individuals, usedModulesOnly: true },
              component: () => import(/* webpackChunkName: "modules" */ '../views/ModulesList.vue')
            },
            {
              path: ':moduleCode',
              name: 'userModuleView',
              meta: { requiresAuth: true, requireActions: [Permissions.MODULESUSE] },
              props: { moduleUsageType: AppModuleUsageType.Individuals, showHistory: true },
              component: () => import(/* webpackChunkName: "modules" */ '../views/ModuleView.vue')
            }
          ]
        },
        {
          path: 'referral-links',
          name: 'referralLinks',
          meta: { requiresAuth: true, requireActions: [14000] },
          component: () => import(/* webpackChunkName: "cabinet.referrals" */ '../views/Cabinet/ReferralLinks.vue')
        }
      ]
    },
    {
      path: '/refill',
      name: 'refill',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "refill" */ '../views/Refill.vue')
    },
    {
      path: '/registration-confirm',
      name: RouterView.RegistrationConfirm,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */ '../views/Auth/RegistrationConfirm.vue')
    },
    {
      path: '/restore-pass-confirm',
      name: RouterView.RestorePassConfirm,
      meta: { recaptchaRequired: true },
      component: () => import(/* webpackChunkName: "login" */ '../views/Auth/RestorePassConfirm.vue')
    },
    {
      path: '/docs/:moduleType/:folderId',
      name: 'docs',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "documents" */ '../views/Documents.vue')
    },
    {
      path: '/document-add/:isBlank?',
      name: RouterView.DocumentAdd,
      meta: { requiresAuth: true, requireActions: [10110] },
      component: () => import(/* webpackChunkName: "documentAdd" */ '../views/DocumentAdd.vue')
    },
    {
      path: '/import-documents',
      name: 'importDocuments',
      meta: { requiresAuth: true, requireActions: [10110] },
      component: () => import(/* webpackChunkName: "importDocuments" */ '../views/ImportDocuments.vue')
    },
    {
      path: '/viewdoc/:moduleType/:docverode/:folderId/:fileFolderCode?',
      name: RouterView.ViewDoc,
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "viewDoc" */ '../views/ViewDoc.vue')
    },
    {
      path: '/d/:documentAccessToken',
      name: 'viewDocViewOnlyMode',
      meta: { requiresAuth: true, requireActions: [13910] },
      beforeEnter: onBeforeEnterViewDocViewOnlyMode,
      component: () => import(/* webpackChunkName: "viewDoc" */ '../views/ViewDoc.vue')
    },
    {
      path: '/docs/:documentVersionCode/:token?',
      name: RouterView.ViewDocUnregUser,
      meta: { recaptchaRequired: true },
      props: true,
      component: () => import(/* webpackChunkName: "viewDocUnregUser" */ '../views/ViewDocUnregUser.vue')
    },
    {
      path: '/view-pdf/:documentVersionFileCode?',
      name: 'viewPdf',
      meta: { requiresAuth: false },
      component: () => import(/* webpackChunkName: "viewPdf" */ '../views/PdfViewer.vue')
    },
    {
      path: '/search/:moduleType/:folderId',
      name: 'search',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "search" */ '../views/Documents.vue')
    },
    {
      path: '/organization',
      name: 'organization',
      meta: {
        requiresAuth: true,
        requireActions: [
          Permissions.ORGDEPGET, Permissions.ORGROLGET, Permissions.ORGUSGET, Permissions.ORGUSERINVG, Permissions.MYORGGET,
          Permissions.ORGCONTRGET, Permissions.ORGABLANKSGET, Permissions.ORGSETTGET, Permissions.ORGVISSETTEDIT
        ]
      },
      component: () => import(/* webpackChunkName: "organisation" */ '../views/Org/Organization.vue'),
      children: [
        {
          path: '/organization/info',
          name: 'info',
          meta: { requiresAuth: true, requireActions: [Permissions.ORGDEPGET] },
          component: () => import(/* webpackChunkName: "organization.info" */ '../views/Org/Info.vue')
        },
        {
          path: '/organization/departments',
          name: 'departments',
          meta: { requiresAuth: true, requireActions: [Permissions.ORGDEPGET] },
          component: () => import(/* webpackChunkName: "organization.departments" */ '../views/Org/Departments.vue')
        },
        {
          path: '/organization/roles',
          name: 'roles',
          meta: { requiresAuth: true, requireActions: [Permissions.ORGROLGET] },
          component: () => import(/* webpackChunkName: "organization.roles" */ '../views/Org/Roles.vue')
        },
        {
          path: '/organization/users',
          name: 'users',
          meta: { requiresAuth: true, requireActions: [Permissions.ORGUSGET] },
          component: () => import(/* webpackChunkName: "organization.users" */ '../views/Org/Users.vue')
        },
        {
          path: '/organization/invitations',
          name: RouterView.OrgInvitations,
          meta: { requiresAuth: true, requireActions: [Permissions.ORGUSERINVG] },
          component: () => import(/* webpackChunkName: "organization.invitations" */ '../views/Org/Invitations.vue')
        },
        {
          path: '/organization/myorganisations',
          name: RouterView.MyOrganisations,
          meta: { requiresAuth: true, requireActions: [Permissions.MYORGGET] },
          component: () => import(/* webpackChunkName: "organization.myOrganizations" */ '../views/Org/MyOrganisations.vue'),
          children: [
            {
              path: ':myOrganisationCode/transfer-documents',
              name: 'transferMyOrganizationDocuments',
              meta: { requiresAuth: true, requireActions: [Permissions.TRANSFERDOCTOORG] },
              component: () => import(
                /* webpackChunkName: "organization.myOrganizations.documents-transfer" */
                '../views/Org/MyOrganisationsDocumentsTransfer.vue')
            }
          ]
        },
        {
          path: '/organization/orgcontragent',
          name: 'orgcontragent',
          meta: { requiresAuth: true, requireActions: [Permissions.ORGCONTRGET] },
          component: () => import(/* webpackChunkName: "organization.organisationContragents" */ '../views/Org/OrganisationContragents.vue')
        },
        {
          path: '/organization/blanksadmin',
          name: 'orgblanksadmin',
          meta: { requiresAuth: true, requireActions: [Permissions.ORGABLANKSGET] },
          component: () => import(/* webpackChunkName: "organization.organisationBlanks" */ '../views/Org/BlanksAdmin.vue')
        },
        {
          path: '/organization/settings',
          name: 'orgsettings',
          meta: { requiresAuth: true, requireActions: [Permissions.ORGSETTGET] },
          component: () => import(/* webpackChunkName: "organization.organisationSettings" */ '../views/Org/Settings.vue')
        },
        {
          path: 'modules',
          name: 'organisationModules',
          redirect: { name: 'orgModulesList' },
          component: () => import(/* webpackChunkName: "modules" */ '../views/Org/OrganizationModules.vue'),
          children: [
            {
              path: '',
              name: 'orgModulesList',
              meta: { requiresAuth: true, requireActions: [Permissions.MODULESUSE] },
              props: { moduleUsageType: AppModuleUsageType.Organisations, usedModulesOnly: true },
              component: () => import(/* webpackChunkName: "modules" */ '../views/ModulesList.vue')
            },
            {
              path: ':moduleCode',
              name: 'orgModuleView',
              meta: { requiresAuth: true, requireActions: [Permissions.MODULESUSE] },
              props: { moduleUsageType: AppModuleUsageType.Organisations, showHistory: true },
              component: () => import(/* webpackChunkName: "modules" */ '../views/ModuleView.vue')
            }
          ]
        },
        {
          path: '/organization/stamps',
          name: RouterView.OrgSignVisSettings,
          meta: { requiresAuth: true, requireActions: [Permissions.ORGVISSETTEDIT] },
          component: () => import(/* webpackChunkName: "organization.stamps" */ '../views/Org/Stamps.vue')
        }
      ]
    },
    {
      path: '/folders',
      name: 'folders',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "fodlers" */ '../views/Folders.vue')
    },
    {
      path: '/filefolders',
      name: 'filefolders',
      redirect: '/filefolders/0',
      meta: { requiresAuth: true, requireActions: [12300] },
      component: () => import(/* webpackChunkName: "filefodlers" */ '../views/FileFolders.vue'),
      children: [
        {
          path: '/filefolders/:folderType/:fileFolder?',
          name: 'filefoldersview',
          meta: { requiresAuth: true, requireActions: [12300] },
          component: () => import(/* webpackChunkName: "filefodlers" */ '../views/FileFoldersContent.vue')
        }
      ]
    },
    {
      path: '/contragents',
      name: 'contragents',
      meta: { requiresAuth: true, requireActions: [11300] },
      component: () => import(/* webpackChunkName: "contragents" */ '../views/Contragents/Contragents.vue')
    },
    {
      path: '/contragents/import',
      name: 'importContragents',
      meta: { requiresAuth: true, requireActions: [11311] },
      component: () => import(/* webpackChunkName: "contragents" */ '../views/Contragents/ImportContragents.vue')
    },
    {
      path: '/notifications',
      name: RouterView.Notifications,
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "notifications" */ '../views/Notifications.vue')
    },
    {
      path: '/news',
      name: 'news',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "notifications" */ '../views/News.vue')
    },
    {
      path: '/logs',
      name: 'logs',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "logs" */ '../views/Logs.vue')
    },
    {
      path: '/admin/clients',
      name: RouterView.Clients,
      meta: { requiresAuth: true, requireActions: [Permissions.A_USERSGET] },
      component: () => import(/* webpackChunkName: "clients" */ '../views/Admin/Clients.vue')
    },
    {
      path: '/admin/clients/:clientCode',
      name: RouterView.ClientDetails,
      meta: { requiresAuth: true, requireActions: [Permissions.A_USERSGET] },
      props: true,
      component: () => import(/* webpackChunkName: "clients" */ '../views/Admin/ClientDetails.vue'),
      redirect: { name: RouterView.AdminOrgUsers },
      children: [
        {
          path: 'users',
          name: RouterView.AdminOrgUsers,
          meta: { requiresAuth: true, requireActions: [Permissions.A_USERSGET] },
          props: true,
          component: () => import(/* webpackChunkName: "users" */'../components/Admin/AdminUsers.vue')
        },
        {
          path: 'users/:userCode',
          name: RouterView.AdminUserDetails,
          meta: { requiresAuth: true, requireActions: [Permissions.A_USERSGET] },
          props: true,
          component: () => import(/* webpackChunkName: "users" */ '../components/Admin/AdminUserDetails.vue')
        },
        {
          path: 'counts',
          name: 'adminOrgCounts',
          meta: { requiresAuth: true, requireActions: [20500] },
          props: true,
          component: () => import(/* webpackChunkName: "counts" */'../components/Admin/AdminCounts.vue')
        },
        {
          path: 'counts/:countCode',
          name: 'adminCountDetails',
          meta: { requiresAuth: true, requireActions: [20500] },
          props: true,
          component: () => import(/* webpackChunkName: "counts" */'../views/Admin/AdminCountDetails.vue')
        },
        {
          path: 'serviceorders',
          name: 'adminOrgServiceOrders',
          meta: { requiresAuth: true, requireActions: [Permissions.A_ORDERGET] },
          props: true,
          component: () => import(/* webpackChunkName: "clientserviceorders" */'../components/Admin/AdminClientServiceOrders.vue')
        },
        {
          path: 'management',
          name: 'adminClientManagement',
          meta: { requiresAuth: true, requireActions: [20000, 21500] },
          props: true,
          component: () => import(/* webpackChunkName: "clients.management" */'../components/Admin/AdminClientManagement.vue')
        },
        {
          path: 'documents',
          name: 'adminClientDocuments',
          meta: { requiresAuth: true, requireActions: [21200] },
          props: true,
          component: () => import(/* webpackChunkName: "clients.adminclientdocuments" */'../components/Admin/AdminClientDocuments.vue')
        },
        {
          path: 'statistic',
          name: RouterView.AdminClientStatistic,
          meta: { requiresAuth: true, requireActions: [21400] },
          props: true,
          component: () => import(/* webpackChunkName: "clients.adminClientStatistic" */'../components/Admin/AdminClientStatistic.vue')
        },
        {
          path: 'notification',
          name: 'adminClientNotification',
          meta: { requiresAuth: true, requireActions: [21600] },
          props: true,
          component: () => import(/* webpackChunkName: "clients.adminClientNotification" */'../components/Admin/AdminClientNotification.vue')
        },
        {
          path: 'modules',
          name: 'adminClientModules',
          meta: { requiresAuth: true, requireActions: [21700] },
          redirect: { name: 'adminModulesList' },
          props: true,
          component: () => import(/* webpackChunkName: "clients.modules" */'../components/Admin/AdminClientModules.vue'),
          children: [{
            path: '',
            name: 'adminModulesList',
            meta: { requiresAuth: true, requireActions: [21700] },
            props: true,
            component: () => import(/* webpackChunkName: "modules" */ '../views/ModulesList.vue')
          },
          {
            path: ':moduleCode',
            name: 'adminModuleView',
            meta: { requiresAuth: true, requireActions: [21700] },
            props: true,
            component: () => import(/* webpackChunkName: "modules" */ '../views/ModuleView.vue')
          }]
        },
        {
          path: 'invitations',
          name: RouterView.AdminClientInvitations,
          meta: { requiresAuth: true, requireActions: [Permissions.A_CLIENTINVITE] },
          props: true,
          component: () => import(/* webpackChunkName: "organization.invitations" */'../views/Org/Invitations.vue')
        }
      ]
    },
    {
      path: '/admin/clients/new/create',
      name: RouterView.ClientNew,
      meta: { requiresAuth: true, requireActions: [Permissions.A_ORGADD] },
      component: () => import(/* webpackChunkName: "admin.client-new" */ '../views/Admin/ClientNew.vue')
    },
    {
      path: '/admin/registration-keys',
      name: 'adminRegistrationKeys',
      meta: { requiresAuth: true, requireActions: [Permissions.A_REGTOKENGET] },
      component: () => import(/* webpackChunkName: "admin.registrationkeys" */ '../views/Admin/RegistrationKeys.vue')
    },
    {
      path: '/admin/advertisement',
      name: 'adminAdvertisement',
      meta: { requiresAuth: true, requireActions: [Permissions.A_ADVGET] },
      component: () => import(/* webpackChunkName: "admin.advertisements" */ '../views/Admin/Advertisement.vue')
    },
    {
      path: '/admin/serviceorders',
      name: 'adminServiceOrders',
      meta: { requiresAuth: true, requireActions: [Permissions.A_ORDERGET] },
      component: () => import(/* webpackChunkName: "serviceorders" */'../components/Admin/AdminServiceOrders.vue')
    },
    {
      path: '/admin/billing',
      name: 'adminBilling',
      meta: { requiresAuth: true, requireActions: [Permissions.A_BILLINGGET] },
      component: () => import(/* webpackChunkName: "serviceorders" */'../components/Admin/AdminBilling.vue')
    },
    {
      path: '/admin/push-notifications',
      name: 'adminPushNotifications',
      meta: { requiresAuth: true, requireActions: [Permissions.A_PUSHSEND] },
      component: () => import(/* webpackChunkName: "admin.pushnotifications" */ '../views/Admin/PushNotifications.vue')
    },
    {
      path: '/admin/reports',
      name: RouterView.AdminReports,
      meta: { requiresAuth: true, requireActions: [Permissions.A_REPORTS] },
      component: () => import(/* webpackChunkName: "admin.reports" */ '../views/Admin/Reports.vue')
    },
    {
      path: '/admin/document-ext-params',
      name: RouterView.AdminDocumentExtParams,
      meta: { requiresAuth: true, requireActions: [Permissions.A_DOCPARAMGET] },
      component: () => import(/* webpackChunkName: "admin.doc-ext-params" */ '../views/Admin/DocumentExtendedParams.vue')
    },
    {
      path: '/admin/document-ext-params/document-types',
      name: RouterView.AdminDocumentTypes,
      meta: { requiresAuth: true, requireActions: [Permissions.A_DOCPARAMGET] },
      component: () => import(/* webpackChunkName: "admin.doc-ext-params" */ '../views/Admin/DocumentTypes.vue'),
      children: [
        {
          path: ':documentTypeCode',
          name: RouterView.AdminDocumentTypeEdit,
          props: true,
          meta: { requiresAuth: true, requireActions: [21920] },
          component: () => import(/* webpackChunkName: "admin.doc-ext-params" */ '../views/Admin/DocumentTypeEdit.vue')
        }
      ]
    },
    {
      path: '/admin/system-integrity',
      name: RouterView.AdminSystemIntegrity,
      meta: { requiresAuth: true, requireActions: [Permissions.A_INTEGRITYGET] },
      component: () => import(/* webpackChunkName: "admin.system-integrity" */ '../views/Admin/SystemIntegrity.vue')
    },
    {
      path: '/admin/promo-actions',
      name: RouterView.AdminPromoActions,
      meta: { requiresAuth: true, requireActions: [Permissions.A_PROMOACTIONGET] },
      component: () => import(/* webpackChunkName: "admin.promo-actions" */ '../views/Admin/PromoActions.vue')
    },
    {
      path: '/admin/promo-actions/:promoActionId/edit',
      name: RouterView.AdminPromoActionDetails,
      meta: { requiresAuth: true, requireActions: [Permissions.A_PROMOACTIONADD] },
      props: true,
      component: () => import(/* webpackChunkName: "admin.promo-actions" */ '../views/Admin/PromoActionDetails.vue')
    },
    {
      path: '/admin/promo-actions/new',
      name: RouterView.AdminPromoActionCreate,
      meta: { requiresAuth: true, requireActions: [Permissions.A_PROMOACTIONADD] },
      props: true,
      component: () => import(/* webpackChunkName: "admin.promo-actions" */ '../views/Admin/PromoActionDetails.vue')
    },
    {
      path: '/admin/iit-files',
      name: RouterView.AdminIitFiles,
      meta: { requiresAuth: true, requireActions: [Permissions.A_IITFILESGET] },
      props: true,
      component: () => import(/* webpackChunkName: "admin.iit-files" */ '../views/Admin/IitFiles.vue')
    },
    {
      path: '/admin/shablon',
      name: 'shablon',
      redirect: '/admin/shablon/documents',
      meta: { requiresAuth: true, requireActions: [Permissions.A_SHTGET, Permissions.A_SHADGET, Permissions.ORGADMSHTGET, Permissions.ORGADMSHADGET] },
      component: () => import(/* webpackChunkName: "admin.shablon-documents" */ '../views/Admin/Shablon/Shablon.vue'),
      children: [
        {
          path: 'documents',
          name: 'documents',
          meta: { requiresAuth: true, requireActions: [Permissions.A_SHADGET, Permissions.ORGADMSHADGET] },
          component: () => import(/* webpackChunkName: "admin.shablon-documents" */ '../views/Admin/Shablon/ShablonDocuments.vue')
        },
        {
          path: 'types',
          name: 'types',
          meta: { requiresAuth: true, requireActions: [Permissions.A_SHTGET, Permissions.ORGADMSHTGET] },
          component: () => import(/* webpackChunkName: "admin.shablon-documents" */ '../views/Admin/Shablon/ShablonType.vue')
        }
      ]
    },
    {
      path: '/admin/shablon/edit/:docShablonGuid/',
      name: 'shablonEdit',
      meta: { requiresAuth: true, requireActions: [Permissions.A_SHADGET, Permissions.ORGADMSHADGET, Permissions.EDU_FORMS_GET] },
      component: () => import(/* webpackChunkName: "admin.shablon-documents" */ '../views/Admin/Shablon/EditShablon.vue')
    },
    {
      path: '/admin/dictionaries',
      name: RouterView.AdminDictionaries,
      meta: { requiresAuth: true, requireActions: [Permissions.A_DICTIONARY_GET] },
      component: () => import(/* webpackChunkName: "admin.dictionaries" */ '../views/Admin/Dictionaries.vue')
    },
    {
      path: '/admin/dictionaries/:dictionaryId',
      name: RouterView.AdminDictionaryView,
      meta: { requiresAuth: true, requireActions: [Permissions.A_DICTIONARY_GET] },
      props: true,
      component: () => import(/* webpackChunkName: "admin.dictionaries" */ '../views/Admin/DictionaryView.vue')
    },
    {
      path: '/admin/edu-dictionaries',
      meta: { requiresAuth: true, requireActions: [Permissions.EDU_DICTIONARY_GET] },
      props: true,
      component: () => import(/* webpackChunkName: "edu" */ '../views/Admin/EduDictionaries.vue'),
      children: [
        {
          path: '',
          name: RouterView.EducationDictionaries,
          meta: { requiresAuth: true, requireActions: [Permissions.EDU_DICTIONARY_GET] },
          component: () => import(/* webpackChunkName: "admin.dictionaries" */ '../views/Admin/Dictionaries.vue')
        },
        {
          path: ':dictionaryId',
          name: RouterView.EducationDictionaryView,
          meta: { requiresAuth: true, requireActions: [Permissions.EDU_DICTIONARY_GET] },
          props: true,
          component: () => import(/* webpackChunkName: "admin.dictionaries" */ '../views/Admin/DictionaryView.vue')
        }
      ]
    },
    {
      path: '/admin/edu-forms',
      meta: { requiresAuth: true, requireActions: [Permissions.EDU_FORMS_GET] },
      props: true,
      component: () => import(/* webpackChunkName: "edu" */ '../views/Admin/EduForms.vue'),
      children: [
        {
          path: '',
          name: RouterView.EducationForms,
          meta: { requiresAuth: true, requireActions: [Permissions.EDU_FORMS_GET] },
          component: () => import(/* webpackChunkName: "admin.shablon-documents" */ '../views/Admin/Shablon/ShablonDocuments.vue')
        }
      ]
    },
    {
      path: '/admin/edu-plans',
      name: RouterView.EducationPlans,
      meta: { requiresAuth: true, requireActions: [Permissions.EDU_PLANS_GET] },
      props: true,
      component: () => import(/* webpackChunkName: "edu" */ '../views/Admin/EduPlans.vue')
    },
    {
      path: '/admin/surveys',
      name: RouterView.AdminSurveys,
      meta: { requiresAuth: true, requireActions: [Permissions.A_SURVEY_GET] },
      props: true,
      component: () => import(/* webpackChunkName: "surveys" */ '../views/Admin/Surveys.vue')
    },
    {
      path: '/admin/surveys/:surveyGuid',
      name: RouterView.AdminSurveyDetails,
      meta: { requiresAuth: true, requireActions: [Permissions.A_SURVEY_GET] },
      props: true,
      component: () => import(/* webpackChunkName: "surveys" */ '../views/Admin/SurveyDetails.vue')
    },
    {
      path: '/utilities',
      redirect: '/utilities/pdf',
      name: 'utilities',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "utilities" */ '../views/Utilities.vue'),
      children: [
        {
          path: 'pdf',
          name: 'pdf',
          meta: { requiresAuth: true },
          component: () => import(/* webpackChunkName: "utilities.pdf" */ '../components/Utilities/ConvertToPdf.vue')
        },
        {
          path: 'compare',
          name: 'compare',
          meta: { requiresAuth: true },
          component: () => import(/* webpackChunkName: "utilities.compare" */ '../components/Utilities/CompareDocs.vue')
        },
        {
          path: 'sign-add',
          name: 'signAdd',
          meta: { requiresAuth: true },
          component: () => import(/* webpackChunkName: "utilities.sign.add" */ '../components/Utilities/SignAdd.vue')
        },
        {
          path: 'sign-check',
          name: 'signCheck',
          meta: { requiresAuth: true },
          component: () => import(/* webpackChunkName: "utilities.sign.check" */ '../components/Utilities/SignCheck.vue')
        },
        {
          path: 'internal-signatures',
          name: RouterView.UtilityInternalSignatures,
          meta: { requiresAuth: true },
          component: () => import(/* webpackChunkName: "utilities.sign.test" */ '../components/Utilities/InternalSignatures.vue')
        },
        {
          path: 'document-edit',
          name: 'documentEdit',
          meta: { requiresAuth: true },
          props: true,
          component: () => import(/* webpackChunkName: "utilities.documentedit" */ '../components/Utilities/DocumentEdit.vue'),
          children: [
            {
              path: 'docx',
              name: 'documentEditDocx',
              meta: { requiresAuth: true },
              component: () => import(/* webpackChunkName: "utilities.documentedit.docx" */ '../components/Utilities/DocumentEdit.vue'),
              props: { editorType: 0 }
            },
            {
              path: 'xlsx',
              name: 'documentEditXlsx',
              meta: { requiresAuth: true },
              component: () => import(/* webpackChunkName: "utilities.documentedit.xlsx" */ '../components/Utilities/DocumentEdit.vue'),
              props: { editorType: 1 }
            }
          ]
        }
      ]
    },
    {
      path: '/rouming/allview',
      name: 'roumingAllView',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "rouming" */'../views/Rouming/RoumingAllView.vue')
    },
    {
      path: '/rouming/create',
      name: 'roumingCreate',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "rouming" */'../views/Rouming/RoumingCreate.vue')
    },
    {
      path: '/support-request',
      name: 'supportRequest',
      component: () => import(/* webpackChunkName: "support" */'../views/SupportRequest.vue')
    },
    {
      path: '/terminate-session',
      name: 'terminateSession',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "terminateSession" */'../views/Auth/TerminateSession.vue')
    },
    {
      path: '/sign-files',
      name: RouterView.SignFiles,
      meta: { requiresAuth: false },
      component: () => import(/* webpackChunkName: "signFile" */'../views/SignFiles.vue')
    },
    {
      path: '/activate-telegram-user/:activationcode',
      name: 'activaTetelegramUser',
      meta: { requiresAuth: true },
      component: () => import(/* webpackChunkName: "Telegram.activateTelegramUser" */ '../components/Telegram/ActivateTelegramUser.vue')
    },
    {
      path: '/modules',
      name: 'modules',
      redirect: { name: 'modulesList' },
      component: () => import(/* webpackChunkName: "modules" */ '../views/Modules.vue'),
      children: [
        {
          path: '',
          name: 'modulesList',
          meta: { requiresAuth: true, requireActions: [Permissions.MODULESUSE] },
          props: { moduleUsageType: AppModuleUsageType.IndividualsAndOrganisations },
          component: () => import(/* webpackChunkName: "modules" */ '../views/ModulesList.vue')
        },
        {
          path: ':moduleCode',
          name: 'moduleView',
          meta: { requiresAuth: true, requireActions: [Permissions.MODULESUSE] },
          props: { moduleUsageType: AppModuleUsageType.IndividualsAndOrganisations },
          component: () => import(/* webpackChunkName: "modules" */ '../views/ModuleView.vue')
        }
      ]
    },
    {
      path: '/edu-registries',
      name: RouterView.EducationRegistries,
      meta: { requiresAuth: true, requireActions: [Permissions.EDU_REGISTRY_GET] },
      component: () => import(/* webpackChunkName: "edu" */ '../views/EduRegistries.vue')
    },
    {
      path: '/edu-registries/:educationDocRegistryUid/documents',
      name: RouterView.EducationDocumentRegistries,
      meta: { requiresAuth: true, requireActions: [Permissions.EDU_REGISTRY_GET] },
      component: () => import(/* webpackChunkName: "edu" */ '../views/EduDocumentRegistries.vue')
    },
    {
      path: '/youcontrol',
      name: RouterView.YouControl,
      meta: { requiresAuth: true, requireModules: [AppModules.YouControl] },
      component: () => import(/* webpackChunkName: "youcontrol" */ '../views/YouControl.vue')
    },
    { path: '/error', name: 'error', component: () => import(/* webpackChunkName: "error" */ '../views/Error.vue') },
    { path: '/404', component: () => import(/* webpackChunkName: "login" */ '../views/NotFound.vue') },
    { path: '*', component: () => import(/* webpackChunkName: "login" */ '../views/NotFound.vue') }
  ]
});

let loader;
router.beforeEach((to, from, next) => {
  // При першому вході на сторінку логін не показуємо спінер оскільки стилі не завантажені і він зеленого кольору
  if (to.fullPath !== '/login' && to.fullPath !== '' && to.fullPath !== '/registration' && to.fullPath !== '/contacts') {
    showLoader();
  }

  let isLoggedIn = store.getters[_act.AUTH_GET_ISLOGINEDGET];
  if (to.meta && (to.meta.requiresAuth || to.meta.requireActions) && !isLoggedIn) {
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }

  if (to.name === RouterView.Home && rolesHelper.isCurrentUserSecurityAdmin) {
    return next({ name: RouterView.Clients });
  }

  if (to.name === RouterView.Home && rolesHelper.isCurrentUserReportsAdmin) {
    return next({ name: RouterView.AdminReports });
  }

  if (isLoggedIn && to.meta && to.meta.requireActions && to.meta.requireActions.length) {
    let userHasActions = permissionsHelper.userPermissions.some(v => to.meta?.requireActions.includes(v));
    if (!userHasActions) {
      return goToNotFound(to, from, next);
    }
  }

  if (isLoggedIn && to.meta && to.meta.requireModules && to.meta.requireModules.length) {
    let userHasModules = userModulesHelper.userModules.some(v => to.meta?.requireModules.includes(v));
    if (!userHasModules) {
      return goToNotFound(to, from, next);
    }
  }

  if (to.name === 'importDocuments' && (isMobile || !store.getters[_act.AUTH_GET_ORGGET])) {
    return goToNotFound(to, from, next);
  }

  if ([
    'userModulesList', 'userModuleView', 'orgModulesList', 'orgModuleView', 'modulesList', 'moduleView', 'adminModulesList',
    'adminModuleView'
  ].includes(to.name || '') &&
      !globalSettingsHelper.isModulesEnabled) {
    return goToNotFound(to, from, next);
  }

  if (['referralLinks'].includes(to.name || '') && !globalSettingsHelper.isReferralLinkEnabled) {
    return goToNotFound(to, from, next);
  }

  if (to.name === RouterView.Registration && !globalSettingsHelper.isRegistrationEnabled) {
    return goToNotFound(to, from, next);
  }

  if (to.name === RouterView.ClientNew && !globalSettingsHelper.isRegistrationByAdminMode) {
    return goToNotFound(to, from, next);
  }

  if ([
    `${RouterView.OrgInvitations}`, `${RouterView.MyOrganisations}`, `${RouterView.AdminClientInvitations}`,
    `${RouterView.Invitations}`
  ].includes(to.name || '') &&
      globalSettingsHelper.isRegistrationByAdminMode) {
    return goToNotFound(to, from, next);
  }

  if (to.name === RouterView.UtilityInternalSignatures && !isDevelopment()) {
    return goToNotFound(to, from, next);
  }

  next();
});

router.afterEach(hideLoader);

router.onError(hideLoader);

function hideLoader() {
  if (loader) {
    loader.hide();
    loader = null;
  }
}

function showLoader(): any {
  hideLoader();
  loader = showLoading();
}

function goToNotFound(to, from, next): void {
  if (from.path === '/404') {
    hideLoader();
  }
  next('/404');
}

export default router;

async function onBeforeEnterViewDocViewOnlyMode(to, from, next) {
  if (to.params.documentAccessToken) {
    try {
      const r = await documentService.validateDocumentAccessToken(to.params.documentAccessToken);
      if (r && r.documentVersionCode) {
        if (!r.documentAccessTokenIsActive) {
          next({ name: 'error', params: { errorLocale: 'errors.documentAccessTokenIsNotActive' } });
        } else if (r.isViewOnlyMode === true) {
          to.params.docverode = r.documentVersionCode;
          to.params.moduleType = eModuleTypes.DocumentsNotProcessed;
          to.params.folderId = 0;
          next();
        } else {
          next({
            name: 'viewdoc',
            params: {
              docverode: r.documentVersionCode,
              moduleType: eModuleTypes.DocumentsNotProcessed,
              folderId: 0
            }
          });
        }
      } else {
        next({ name: 'error', params: { errorLocale: 'errors.documentNotFound' } });
      }
    } catch (error) {
      next('home');
    }
  } else {
    next('404');
  }
}
